import React, { FC, useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { isEmpty } from 'lodash';
import { Box, Button, Typography, CircularProgress } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';

import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { getTasksByID, updateTaskStatus } from '../../services/TasksService';
import { queryKeys } from '../../react-query/constants';
import TaskForm from './TaskForm';
import LoaderSpinner from '../../components/LoaderSpinner';
import BackButton from '../../components/BackButton';
import { useStyles } from './styles';
import { TaskDto } from '../../api/data-contracts';
import { TaskStatusValues } from '../../models/enums/TaskStatusValues';
import { getBackendErrorMessage } from '../../config/utils';

const TaskFormPage: FC = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { id } = useParams<{ id?: string }>();

    const [submitForm, setSubmitForm] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const { data: task, isLoading } = useQuery<TaskDto>(
        [queryKeys.task, id],
        () => getTasksByID(parseInt(id as string, 10)),
        {
            enabled: !isEmpty(id),
        },
    );

    const toggleStatusMutation = useMutation(
        async (taskId: number) => {
            const nextStatus =
                task?.status?.id === TaskStatusValues.CLOSED
                    ? TaskStatusValues.OPEN
                    : TaskStatusValues.CLOSED;
            return updateTaskStatus(taskId, nextStatus);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.task]);
                enqueueSnackbar(t('Status updated'), { variant: 'success' });
            },
            onError: (error: unknown) => {
                const errorMessage =
                    error instanceof Error
                        ? getBackendErrorMessage(error as AxiosError)
                        : 'Unknown error occurred';
                enqueueSnackbar(
                    t(`errorsEn:${typeof errorMessage === 'string' ? errorMessage : 'Unknown error'}`),
                    {
                        variant: 'error',
                    },
                );
            },
        },
    );

    const toggleStatus = useCallback(() => {
        if (!task?.id || toggleStatusMutation.isLoading) return;
        toggleStatusMutation.mutate(task.id);
    }, [toggleStatusMutation, task]);

    const renderToggleStatusButton = useCallback(
        () => (
            <Button
                className={classes.statusButton}
                onClick={toggleStatus}
                disabled={toggleStatusMutation.isLoading}
            >
                <Box
                    className={clsx(classes.taskStatusBox, {
                        [classes.taskStatusBoxDone]: task?.status?.name === 'Closed',
                    })}
                >
                    {(() => {
                        if (toggleStatusMutation.isLoading) {
                            return <CircularProgress color="secondary" size="1rem" />;
                        }
                        if (task?.status?.id === TaskStatusValues.CLOSED) {
                            return <CheckIcon className={classes.taskStatusBoxIcon} />;
                        }
                        return null;
                    })()}
                </Box>
            </Button>
        ),
        [toggleStatus, classes, task, toggleStatusMutation.isLoading],
    );

    const key = useMemo(() => `task-${task?.id}-${task?.status?.name}`, [task]);

    return (
        <Box width="100%">
            <Box>
                <BackButton label="back to tasks" />

                <Box className={classes.pageHeader}>
                    <Typography color="primary" className={classes.pageSubTitle}>
                        {task?.id ? 'editing task:' : 'adding task'}
                    </Typography>

                    <Box className={classes.headerRow}>
                        {task?.id ? (
                            <Typography className={classes.pageTitle}>
                                {task.name} {renderToggleStatusButton()}
                            </Typography>
                        ) : (
                            <Box />
                        )}

                        <Box className={classes.buttons}>
                            <Button
                                variant="contained"
                                color="primary"
                                endIcon={isSubmitting ? null : <CheckIcon />}
                                className={classes.saveButton}
                                onClick={() => {
                                    if (isSubmitting) return;
                                    setError(null);
                                    setSubmitForm(true);
                                }}
                                disabled={(isLoading && !isEmpty(id)) || isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="secondary" size="2rem" /> : 'save'}
                            </Button>
                            {error && <Typography color="error">{error}</Typography>}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {isLoading && !isEmpty(id) ? (
                <LoaderSpinner />
            ) : (
                <TaskForm
                    key={key}
                    task={task}
                    submitForm={submitForm}
                    setSubmitForm={setSubmitForm}
                    setIsSubmitting={setIsSubmitting}
                    onError={err => {
                        const errorStr = typeof err === 'object' ? JSON.stringify(err) : String(err);
                        setError(errorStr);
                        setIsSubmitting(false);
                    }}
                />
            )}
        </Box>
    );
};

export default TaskFormPage;
